import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { postReservationAccept, updateReservationStatus, sendTableReadyMessage } from "../../actions/restaurantActions";
import { fetchCancelReservation, checkCancelAction } from "../../actions/reservationsActions";
import { statusToGroup, iconStatusMap, ModernButton } from "../DefaultImports";
import CompareChanges from "../CompareChanges";
import StatusDropdown from "../StatusDropdown";
import { LuCheck, LuEye, LuX } from "react-icons/lu";
import ReservationStatusIcon from "../../icons/ReservationStatusIcon";
import { acceptedStatuses, pendingStatuses, getReservationStatusAbreviationTranslation, getReservationStatusTooltipTranslationForRestaurant } from "../ReservationStatus";
import Tooltip from "../ToolTipWrapper";
import { CustomModal } from "../CustomModal";
const DEFAULT_STATUS_COLOR = "#6b8c88";
const STATUS_COLORS = {
  "A CHEGAR": "#2F80ED",
  ATRASADA: "#D97706",
  "À PORTA": "#7C3AED",
  REST: "#0F766E",
  CLIENTE: "#2563EB",
  ESPERA: "#B45309",
  FINALIZADA: "#64748B",
  "Mesa livre": "#16A34A",
  CANCELADAS: "#DC2626",
  TOTAL: "#334155"
};
const getStatusColor = (iconData, statusGroup) => {
  const variationColor = iconData?.color_variation_1?.trim?.();
  if (variationColor) return variationColor;
  const normalColor = iconData?.color?.trim?.();
  if (normalColor) return normalColor;
  return STATUS_COLORS[statusGroup] || DEFAULT_STATUS_COLOR;
};
const LineWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  background: #ffffff;
  color: #99b0ad;
  padding: ${({ $compact }) => ($compact ? "2px 4px 2px 8px" : "0 0 0 6px")};
  width: 100%;
  min-height: ${({ $compact }) => ($compact ? "38px" : "34px")};
  border-top: ${({ $compact }) =>
    $compact ? "0" : "1px solid rgba(153, 176, 173, 0.18)"};
  border-left: ${({ $compact }) =>
    $compact ? "1px solid #e7eceb" : "0"};
  background: ${({ $compact }) => ($compact ? "#fbfcfc" : "#ffffff")};
`;
const StatusIconSpan = styled.span`
  width: 28px;
  min-width: 28px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({
  $color
}) => $color || DEFAULT_STATUS_COLOR};
  cursor: pointer;
  margin-right: 4px;

  svg {
    width: 18px;
    height: 18px;
    max-width: 18px;
    max-height: 18px;
    color: inherit;
    stroke-width: 1.75;
  }
`;
const StatusDropdownWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  font-size: 10px !important;
`;
const IconButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: #99b0ad;
  width: 50%;
  min-height: 34px;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease,
    transform 0.1s ease;
  border-left: 1px solid rgba(153, 176, 173, 0.18);

  svg {
    width: 18px;
    height: 18px;
    max-width: 18px;
    max-height: 18px;
    stroke-width: 1.75;
  }

  &:hover {
    background-color: rgba(238, 117, 76, 0.08);
    color: #ee754c;
    transform: scale(1.04);
  }
`;
const InBetween = styled.div`
  width: 100%;
  min-width: 0;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
`;
const StatusActions = styled.div`
  height: 100%;
  width: ${({ $narrow }) => $narrow ? "88px" : "150px"};
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  @media (max-width: 768px) { width: 88px; }
`;
function ReservationStatusChanger({
  reservation,
  past_estimated_time,
  isNotifications = false,
  compact = false,
  narrow = false
}) {
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAttendedModal, setShowAttendedModal] = useState(false);
  const [showNotAttendedModal, setShowNotAttendedModal] = useState(false);
  const [showLeftModal, setShowLeftModal] = useState(false);
  const [rejectEdition, setRejectEdition] = useState(false);
  const [reservationBeforeEdition, setReservationBeforeEdition] = useState(null);
  const [notify_customer, setNotify_customer] = useState(true);
  const [sendTableReadyMsg, setSendTableReadyMsg] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState("");
  const currentStatusGroup = statusToGroup[reservation?.status];
  const currentIconData = iconStatusMap[currentStatusGroup] || {
    label: currentStatusGroup || reservation?.status || t("ui.i18nFinalCleanup.status")
  };
  const currentStatusColor = getStatusColor(currentIconData, currentStatusGroup);
  useEffect(() => {
    if (reservation?.history && reservation.restore_version_id !== undefined) {
      const item = reservation.history.find(h => h.history_id === reservation.restore_version_id);
      setReservationBeforeEdition(item || null);
    } else {
      setReservationBeforeEdition(null);
    }
  }, [reservation]);
  const runReservationAction = async (action, onSuccess) => {
    if (isSubmitting) return false;
    setIsSubmitting(true);
    setActionError("");
    try {
      const result = await dispatch(action);
      if (result?.ok) {
        if (onSuccess) onSuccess();
        return true;
      }
      if (!result?.busy) {
        setActionError(
          result?.message ||
            t("ui.i18nFinalCleanup.genericTryAgainError", {
              defaultValue: "Ocorreu um erro. Tenta novamente.",
            }),
        );
      }
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleAccept = async () => {
    await runReservationAction(
      postReservationAccept(
        reservation.token,
        false,
        rejectEdition,
        notify_customer,
      ),
      () => setShowAcceptModal(false),
    );
  };
  const handleCancelCheck = () => {
    if (isSubmitting) return;
    dispatch(checkCancelAction(reservation.token));
    setShowAcceptModal(false);
  };
  const handleCancel = async () => {
    await runReservationAction(
      fetchCancelReservation(reservation.id, false, false, rejectEdition),
      () => setShowCancelModal(false),
    );
  };
  const handleNotAttended = async () => {
    await runReservationAction(
      updateReservationStatus(reservation.id, "no_show"),
      () => setShowNotAttendedModal(false),
    );
  };
  const handleLeft = async () => {
    await runReservationAction(
      updateReservationStatus(reservation.id, "completed"),
      () => setShowLeftModal(false),
    );
  };
  const isWaitingList = reservation?.status === "waiting_list";
  useEffect(() => {
    if (showAttendedModal && isWaitingList) {
      setSendTableReadyMsg(true);
    }
  }, [showAttendedModal, isWaitingList]);
  const handleAttendedWithOptionalMessage = async () => {
    const succeeded = await runReservationAction(
      updateReservationStatus(reservation.id, "attended"),
      () => setShowAttendedModal(false),
    );
    if (succeeded && reservation?.status === "waiting_list" && sendTableReadyMsg) {
      dispatch(sendTableReadyMessage(reservation.token));
    }
  };
  const actionErrorMessage = actionError ? (
    <p style={{ color: "#D94B4B", margin: "10px 0 0", fontSize: "13px" }}>
      {actionError}
    </p>
  ) : null;
  return <LineWrapper $compact={compact}>
      <Tooltip text={getReservationStatusTooltipTranslationForRestaurant(reservation.status)}>
        <StatusIconSpan $color={currentStatusColor}>
          <ReservationStatusIcon status={currentStatusGroup} />
        </StatusIconSpan>
      </Tooltip>

      <InBetween>
        <StatusDropdownWrapper>
          <StatusDropdown reservation={reservation} allowedStatuses={["accepted", "pending_restaurant", "late", "waiting_list", "attended", "completed", "no_show", "cancelled_restaurant", "cancelled_customer"]} getStatusColor={() => currentStatusColor} getStatusLabel={getReservationStatusAbreviationTranslation} onSelectStatus={(status, options = {}) => {
          runReservationAction(updateReservationStatus(reservation.id, status, options));
        }} />
        </StatusDropdownWrapper>

        <StatusActions $narrow={isNotifications || narrow}>
          {pendingStatuses.includes(reservation.status) && <>
              <IconButton onClick={() => {
            setRejectEdition(false);
            setActionError("");
            setShowAcceptModal(true);
          }}>
                <LuCheck />
              </IconButton>

              <IconButton onClick={() => {
            setRejectEdition(false);
            setActionError("");
            setShowCancelModal(true);
          }}>
                <LuX />
              </IconButton>
            </>}

          {reservation.status === "edited" && <>
              <IconButton onClick={() => {
            setRejectEdition(true);
            setActionError("");
            setShowAcceptModal(true);
          }}>
                <Tooltip text={getReservationStatusTooltipTranslationForRestaurant("Edited_accept")}>
                  <LuCheck />
                </Tooltip>
              </IconButton>

              <IconButton onClick={() => {
            setRejectEdition(true);
            setActionError("");
            setShowCancelModal(true);
          }}>
                <Tooltip text={getReservationStatusTooltipTranslationForRestaurant("Edited_reject")}>
                  <LuX />
                </Tooltip>
              </IconButton>
            </>}

          {acceptedStatuses.includes(reservation.status) && <>
              <IconButton onClick={() => { setActionError(""); setShowAttendedModal(true); }}>
                <LuCheck />
              </IconButton>

              <IconButton onClick={() => { setActionError(""); setShowNotAttendedModal(true); }}>
                <LuX />
              </IconButton>
            </>}

          {reservation.status === "attended" && <IconButton onClick={() => { setActionError(""); setShowLeftModal(true); }} style={{
          width: "100%"
        }}>
              <LuCheck />
            </IconButton>}

          {reservation.cancelled_warning && <IconButton onClick={() => handleCancelCheck()} style={{
          width: "100%"
        }}>
              <LuEye />
            </IconButton>}
        </StatusActions>
      </InBetween>

      <CustomModal show={showAcceptModal} onHide={() => { if (!isSubmitting) setShowAcceptModal(false); }} title={t("ui.components.reservations.reservationStatusChanger.aceitarReserva")} body={<>{rejectEdition && reservationBeforeEdition ? <CompareChanges before={reservationBeforeEdition} after={reservation} /> : <p>{t("ui.components.reservations.reservationStatusChanger.tensACertezaQuePretendesAceitar")}</p>}{actionErrorMessage}</>} footer={<>
            <ModernButton color="#99b0ad" fontSize="14px" disabled={isSubmitting} onClick={() => setShowAcceptModal(false)}>{t("ui.components.reservations.reservationStatusChanger.cancelar")}</ModernButton>
            <ModernButton color="#EE754C" fontSize="14px" disabled={isSubmitting} onClick={handleAccept}>{isSubmitting ? t("ui.i18nFinalCleanup.processing") : t("ui.components.reservations.reservationStatusChanger.aceitarReserva2")}</ModernButton>
          </>} />

      <CustomModal show={showCancelModal} onHide={() => { if (!isSubmitting) setShowCancelModal(false); }} title={t("ui.components.reservations.reservationStatusChanger.rejeitarReserva")} body={<>{rejectEdition && reservationBeforeEdition ? <CompareChanges before={reservationBeforeEdition} after={reservation} /> : <p>{t("ui.components.reservations.reservationStatusChanger.tensACertezaQuePretendesRejeitar")}</p>}{actionErrorMessage}</>} footer={<>
            <ModernButton color="#99b0ad" fontSize="14px" disabled={isSubmitting} onClick={() => setShowCancelModal(false)}>{t("ui.components.reservations.reservationStatusChanger.cancelar2")}</ModernButton>
            <ModernButton color="#EE754C" fontSize="14px" disabled={isSubmitting} onClick={handleCancel}>{isSubmitting ? t("ui.i18nFinalCleanup.processing") : t("ui.components.reservations.reservationStatusChanger.rejeitarReserva2")}</ModernButton>
          </>} />

      <CustomModal show={showAttendedModal} onHide={() => { if (!isSubmitting) setShowAttendedModal(false); }} title={t("ui.components.reservations.reservationStatusChanger.confirmarPresenca")} body={<>
            <p>{t("ui.components.reservations.reservationStatusChanger.tensACertezaQuePretendesAlterar")}{" "}
              <strong>{t("ui.components.reservations.reservationStatusChanger.compareceu")}</strong>?
            </p>

            {actionErrorMessage}
            {isWaitingList && <label style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        marginTop: "12px",
        cursor: "pointer",
        userSelect: "none"
      }}>
                <input type="checkbox" checked={sendTableReadyMsg} onChange={e => setSendTableReadyMsg(e.target.checked)} style={{
          accentColor: "#EE754C"
        }} />
                <span style={{
          fontWeight: 700
        }}>{t("ui.components.reservations.reservationStatusChanger.enviarMensagemDeAvisoAoCliente")}</span>
              </label>}
          </>} footer={<>
            <ModernButton color="#99b0ad" fontSize="14px" disabled={isSubmitting} onClick={() => setShowAttendedModal(false)}>{t("ui.components.reservations.reservationStatusChanger.naoConfirmar")}</ModernButton>

            <ModernButton color="#EE754C" fontSize="14px" disabled={isSubmitting} onClick={handleAttendedWithOptionalMessage}>{isSubmitting ? t("ui.i18nFinalCleanup.processing") : t("ui.components.reservations.reservationStatusChanger.confirmarPresenca2")}</ModernButton>
          </>} />

      <CustomModal show={showNotAttendedModal} onHide={() => { if (!isSubmitting) setShowNotAttendedModal(false); }} title={t("ui.components.reservations.reservationStatusChanger.confirmarAusencia")} body={<><p>{t("ui.components.reservations.reservationStatusChanger.tensACertezaQuePretendesAlterar2")}{" "}
            <strong>{t("ui.components.reservations.reservationStatusChanger.naoCompareceu")}</strong>?
          </p>{actionErrorMessage}</>} footer={<>
            <ModernButton color="#99b0ad" fontSize="14px" disabled={isSubmitting} onClick={() => setShowNotAttendedModal(false)}>{t("ui.components.reservations.reservationStatusChanger.naoConfirmar2")}</ModernButton>
            <ModernButton color="#EE754C" fontSize="14px" disabled={isSubmitting} onClick={handleNotAttended}>{isSubmitting ? t("ui.i18nFinalCleanup.processing") : t("ui.components.reservations.reservationStatusChanger.confirmarAusencia2")}</ModernButton>
          </>} />

      <CustomModal show={showLeftModal} onHide={() => { if (!isSubmitting) setShowLeftModal(false); }} title={t("ui.components.reservations.reservationStatusChanger.libertarMesa")} body={<><p>{t("ui.components.reservations.reservationStatusChanger.tensACertezaQuePretendesLibertar")}</p>{actionErrorMessage}</>} footer={<>
            <ModernButton color="#99b0ad" fontSize="14px" disabled={isSubmitting} onClick={() => setShowLeftModal(false)}>{t("ui.components.reservations.reservationStatusChanger.nao")}</ModernButton>
            <ModernButton color="#EE754C" fontSize="14px" disabled={isSubmitting} onClick={handleLeft}>{isSubmitting ? t("ui.i18nFinalCleanup.processing") : t("ui.components.reservations.reservationStatusChanger.sim")}</ModernButton>
          </>} />
    </LineWrapper>;
}
/* Memoised: the reservation list renders every row on any change to the
   selection, so a row whose booking is unchanged must not pay for it. */
export default React.memo(ReservationStatusChanger);
